/**
 * Logical sections
 * ────────────────
 * A document contract stores a flat list of block-level `DocumentChild` nodes
 * per page section. Authors think in *logical sections* instead: a heading plus
 * the run of content beneath it (a narrative block, a data table, literal prose,
 * a signature block …). This module is the lens between the two:
 *
 *   parseLogicalSections(contract)     flat children    → logical sections
 *   serializeLogicalSections(base, …)  logical sections → flat children
 *   inferContentType(body)             classify a section by the blocks beneath it
 *
 * It is intentionally generic (no SOW/brand specifics) so both the editor UI
 * (apps/web) and server-side tooling share one model. A `Title` heading is
 * treated as cover / title-page chrome, NOT a section boundary, so the leading
 * cover content (logo, title, client table …) collapses into one "preamble".
 */
/** A ParagraphChild is a text run when it carries no discriminating `type` tag. */
function isTextRun(run) {
    return !('type' in run);
}
/** Join a paragraph's text runs into a plain string (image / page-field runs are skipped). */
export function flattenParagraphText(p) {
    return p.children
        .filter(isTextRun)
        .map((r) => r.text ?? '')
        .join('')
        .trim();
}
/** A heading paragraph that starts a logical section (Title is cover chrome, not a boundary). */
function isSectionHeading(child) {
    return child.type === 'paragraph' && !!child.heading && child.heading !== 'Title';
}
function headingLevelOf(p) {
    if (!p.heading || p.heading === 'Title')
        return null;
    const n = Number(p.heading.slice(7)); // 'Heading3' → 3
    return Number.isFinite(n) ? n : null;
}
/** An empty spacer paragraph (no meaningful text, no image/field run). */
function isEmptyParagraph(child) {
    if (child.type !== 'paragraph')
        return false;
    return child.children.every((run) => (isTextRun(run) ? !run.text?.trim() : false));
}
/**
 * Iterate a section body's blocks, looking through static `table` cells as well: a
 * contentBlock may be embedded in a layout table (e.g. a boxed "Development Summary"
 * cell wrapping the AI overview). Every consumer that scans a body for
 * contentBlock/dynamicBlock nodes must use this, or a table-nested block silently
 * disappears from that consumer (the renderers already recurse into cells).
 */
export function* iterateBodyBlocks(body) {
    for (const child of body) {
        yield child;
        if (child.type === 'table') {
            for (const row of child.rows ?? []) {
                for (const cell of row.children ?? []) {
                    yield* iterateBodyBlocks(cell.children ?? []);
                }
            }
        }
    }
}
/**
 * Classify a section body by the blocks beneath its heading. A single special
 * block → that type; literal prose only → 'static'; literal prose alongside a
 * special block → 'mixed'. Page breaks are layout modifiers, not content.
 */
export function inferContentType(body) {
    let hasLiteral = false;
    let hasBreak = false;
    const specials = new Set();
    for (const child of body) {
        if (isEmptyParagraph(child))
            continue;
        switch (child.type) {
            case 'contentBlock':
                specials.add('ai');
                break;
            case 'dynamicBlock':
                specials.add('table');
                break;
            case 'signatureBlock':
                specials.add('signature');
                break;
            case 'toc':
                specials.add('toc');
                break;
            case 'pageBreak':
                hasBreak = true;
                break;
            default: // non-empty paragraph or literal table
                hasLiteral = true;
                break;
        }
    }
    if (specials.size === 0)
        return hasLiteral ? 'static' : hasBreak ? 'break' : 'static';
    if (specials.size === 1 && !hasLiteral)
        return [...specials][0] ?? 'static';
    return 'mixed';
}
/**
 * The single definition of `when` falsiness, shared by every renderer and section splitter.
 *
 * Keep this the ONLY place the rule lives: the docx renderer, the browser renderer, and the
 * logical-section splitter must agree, or the exported document and the on-screen SOW disagree
 * about which variant of a section the reader sees.
 */
export function whenValueIsTruthy(resolved) {
    const v = resolved.trim().toLowerCase();
    return v !== '' && v !== 'false' && v !== '0' && v !== 'null' && v !== 'undefined';
}
/**
 * Split a contract's flat children into logical sections. Each `Heading1-6`
 * paragraph starts a new section; content before the first such heading (incl.
 * `Title` paragraphs) becomes a single preamble per page section.
 */
export function parseLogicalSections(contract) {
    const out = [];
    let counter = 0;
    const pageSections = Array.isArray(contract.sections) ? contract.sections : [];
    pageSections.forEach((pageSection, pi) => {
        const children = Array.isArray(pageSection.children) ? pageSection.children : [];
        let current = null;
        const flush = () => {
            if (!current)
                return;
            current.contentType = inferContentType(current.body);
            out.push(current);
            current = null;
        };
        for (const child of children) {
            if (isSectionHeading(child)) {
                flush();
                current = {
                    id: `sec-${counter++}`,
                    pageSectionIndex: pi,
                    isPreamble: false,
                    heading: child,
                    headingText: flattenParagraphText(child),
                    headingLevel: headingLevelOf(child),
                    contentType: 'static',
                    body: [],
                };
            }
            else {
                if (!current) {
                    // Leading cover/title content before the first real heading → preamble.
                    current = {
                        id: `sec-${counter++}`,
                        pageSectionIndex: pi,
                        isPreamble: true,
                        heading: null,
                        headingText: '',
                        headingLevel: null,
                        contentType: 'static',
                        body: [],
                    };
                }
                current.body.push(child);
            }
        }
        flush();
    });
    // Label each preamble from any Title paragraphs it carries (e.g. "NetSuite · SOW").
    for (const section of out) {
        if (section.isPreamble && !section.headingText) {
            section.headingText = section.body
                .filter((c) => c.type === 'paragraph' && c.heading === 'Title')
                .map(flattenParagraphText)
                .filter(Boolean)
                .join(' · ');
        }
    }
    return out;
}
/**
 * Rebuild a contract from edited logical sections. Clones `base` (preserving all
 * non-section fields - document metadata, bindings, page properties, and any
 * unknown top-level keys) and replaces each page section's `children` with the
 * flattened `[heading, ...body]` of its logical sections, in array order.
 */
export function serializeLogicalSections(base, sections) {
    const clone = JSON.parse(JSON.stringify(base));
    const pageSections = Array.isArray(clone.sections) ? clone.sections : [];
    const byPage = new Map();
    for (const section of sections) {
        const list = byPage.get(section.pageSectionIndex);
        if (list)
            list.push(section);
        else
            byPage.set(section.pageSectionIndex, [section]);
    }
    byPage.forEach((logical, pi) => {
        const pageSection = pageSections[pi];
        if (!pageSection)
            return;
        pageSection.children = logical.flatMap((s) => s.heading ? [s.heading, ...s.body] : [...s.body]);
    });
    return clone;
}
//# sourceMappingURL=logical-sections.js.map